import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Metrics Dashboard — admin-only view of the metrics_events stream.
 *
 * Reads /api/admin/metrics/summary in a single round-trip and renders
 * three headline KPIs plus a per-event-name catalog with daily sparklines.
 *
 * Access posture: gated by superadmin/superuser at the API layer; the
 * page itself does a session role check on mount and redirects on miss.
 */

// ────────────────────────────────────────────────────────────────────────
// Small presentational helpers
// ────────────────────────────────────────────────────────────────────────

function formatSeconds(s) {
  if (s == null || !Number.isFinite(s)) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

function formatMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatPercent(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(1)}%`;
}

function KpiCard({ label, value, sub, tone = 'slate', help }) {
  const toneMap = {
    slate:   'text-slate-900',
    emerald: 'text-emerald-700',
    amber:   'text-amber-700',
    rose:    'text-orange-700',
    indigo:  'text-indigo-700',
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5 flex flex-col gap-1">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-black ${toneMap[tone] || toneMap.slate}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
      {help && <p className="text-[11px] text-slate-400 mt-1">{help}</p>}
    </div>
  );
}

/**
 * Tiny inline sparkline rendered with SVG. No third-party chart deps —
 * we don't need them for a single-color line over ≤365 points and we
 * keep the bundle smaller by not pulling in recharts/d3 just for this.
 */
function Sparkline({ series, width = 140, height = 32, color = '#6366f1' }) {
  if (!series || series.length === 0) {
    return <span className="text-xs text-slate-400">no data</span>;
  }
  const values = series.map(p => p.count);
  const max = Math.max(...values, 1);
  const step = series.length > 1 ? width / (series.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = values[values.length - 1];
  return (
    <div className="flex items-center gap-2">
      <svg width={width} height={height} className="overflow-visible">
        <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      </svg>
      <span className="text-xs font-semibold text-slate-600 tabular-nums">{last}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Page component
// ────────────────────────────────────────────────────────────────────────

export default function MetricsDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessionChecking, setSessionChecking] = useState(true);

  const [windowDays, setWindowDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState({}); // { event_name: [{day,count}] }
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Session check ──
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success) { navigate('/login'); return; }
        const role = (data.user.role || '').toLowerCase();
        if (!['superadmin'].includes(role)) {
          navigate('/');
          return;
        }
        setUser(data.user);
      } catch {
        navigate('/login');
      }
      setSessionChecking(false);
    })();
  }, [navigate]);

  // ── Data load ──
  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [sRes, hRes] = await Promise.all([
        fetch(`${API}/api/admin/metrics/summary?days=${windowDays}`, { credentials: 'include' }),
        fetch(`${API}/api/admin/metrics/health`, { credentials: 'include' }),
      ]);
      const sData = await sRes.json();
      const hData = await hRes.json();
      if (!sData.success) throw new Error(sData.message || 'Failed to load summary');
      setSummary(sData.data);
      setHealth(hData.sources || []);

      // Fetch sparkline series for the top 6 events by count.
      const top = (sData.data.event_counts || []).slice(0, 6);
      const fetched = await Promise.all(top.map(async (row) => {
        try {
          const r = await fetch(`${API}/api/admin/metrics/series/${row.event_name}?days=${windowDays}`, { credentials: 'include' });
          const d = await r.json();
          return [row.event_name, d.series || []];
        } catch { return [row.event_name, []]; }
      }));
      setSeries(Object.fromEntries(fetched));
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [user, windowDays]);

  useEffect(() => { load(); }, [load]);

  // ── Derived display values ──
  const headline = summary?.headline_kpis;
  const timings = summary?.operational_timings;
  const eventRows = summary?.event_counts || [];

  const memoLatency = useMemo(() => {
    const m = headline?.memo_to_action_latency;
    if (!m || m.observations === 0) return { p50: '—', p90: '—', n: 0 };
    return {
      p50: formatSeconds(m.p50_seconds),
      p90: formatSeconds(m.p90_seconds),
      n: m.observations,
    };
  }, [headline]);

  const routing = useMemo(() => {
    const r = headline?.routing_accuracy;
    if (!r || r.total === 0) return { pct: '—', total: 0, overridden: 0 };
    return {
      pct: formatPercent(r.accuracy_ratio),
      total: r.total,
      overridden: r.overridden,
    };
  }, [headline]);

  const cap = useMemo(() => {
    const c = headline?.cap_closure;
    if (!c || c.opened_count === 0) return { rate: '—', median: '—', opened: 0, closed: 0 };
    return {
      rate: formatPercent(c.opened_count > 0 ? c.closed_count / c.opened_count : 0),
      median: formatSeconds(c.median_close_seconds),
      opened: c.opened_count,
      closed: c.closed_count,
    };
  }, [headline]);

  if (sessionChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Checking session…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900">Metrics</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Operational telemetry across the suite — rolling {windowDays}-day window
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={windowDays}
              onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
              className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700"
              aria-label="Time window"
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
            <button
              onClick={load}
              className="text-sm font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
            >
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="rounded-xl bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Headline KPIs */}
        <section>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Headline KPIs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              label="Memo → action assigned"
              value={memoLatency.p50}
              sub={`p90 ${memoLatency.p90} · ${memoLatency.n} observations`}
              tone="indigo"
              help="Median wall-clock time from memo received to first owner assigned."
            />
            <KpiCard
              label="Routing accuracy"
              value={routing.pct}
              sub={`${routing.total} confirmations · ${routing.overridden} overrides`}
              tone={routing.pct === '—' ? 'slate' : (parseFloat(routing.pct) >= 90 ? 'emerald' : 'amber')}
              help="Share of POM routings the compliance team accepted as-is."
            />
            <KpiCard
              label="CAP closure rate"
              value={cap.rate}
              sub={`${cap.closed}/${cap.opened} closed · median ${cap.median}`}
              tone={cap.rate === '—' ? 'slate' : (parseFloat(cap.rate) >= 80 ? 'emerald' : 'amber')}
              help="Of CAPs opened in window, what % have a matching close event."
            />
          </div>
        </section>

        {/* Operational timings */}
        <section>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">System latencies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Document analysis</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-slate-400">p50</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_analyzed?.p50_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">p90</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_analyzed?.p90_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">p99</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_analyzed?.p99_ms)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">{timings?.doc_analyzed?.observations || 0} observations</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Document routing</p>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-slate-400">p50</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_routed?.p50_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">p90</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_routed?.p90_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">p99</p>
                  <p className="text-lg font-bold text-slate-800">{formatMs(timings?.doc_routed?.p99_ms)}</p>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">{timings?.doc_routed?.observations || 0} observations</p>
            </div>
          </div>
        </section>

        {/* Top events with sparklines */}
        <section>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
            Event volume (top in window)
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3">Event</th>
                  <th className="text-right px-5 py-3">Count</th>
                  <th className="text-left px-5 py-3 hidden sm:table-cell">Trend</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eventRows.length === 0 ? (
                  <tr><td colSpan={3} className="text-center text-sm text-slate-400 py-10">
                    {loading ? 'Loading…' : 'No events recorded in window yet — hooks may still be warming up.'}
                  </td></tr>
                ) : eventRows.map(row => (
                  <tr key={row.event_name} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-xs text-slate-700">{row.event_name}</td>
                    <td className="px-5 py-3 text-right font-bold text-slate-800 tabular-nums">{row.count}</td>
                    <td className="px-5 py-3 hidden sm:table-cell">
                      {series[row.event_name] ? <Sparkline series={series[row.event_name]} /> : <span className="text-xs text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Stream health */}
        <section>
          <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
            Stream health (last 24h)
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
            {health.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">
                No emissions in the last 24 hours from any source.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3">Source app</th>
                    <th className="text-right px-5 py-3">Events (24h)</th>
                    <th className="text-right px-5 py-3">Last event</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {health.map(row => (
                    <tr key={row.source_app} className="hover:bg-slate-50">
                      <td className="px-5 py-3 font-semibold text-slate-700">{row.source_app}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{row.events_24h}</td>
                      <td className="px-5 py-3 text-right text-slate-500 text-xs">
                        {row.last_event_at ? new Date(row.last_event_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <p className="text-[11px] text-slate-400 text-center pt-2 pb-8">
          Source: <code className="font-mono">metrics_events</code> (migration 118). Append-only · 13-month retention · SOC 2 CC4.1 / CC7.2 / A1.1.
        </p>
      </main>
    </div>
  );
}
